import React, { useEffect, useState } from 'react';
import { getSiteStats } from '../api/client';

/**
 * Honest social proof.
 *
 * With a handful of accounts on the system, invented testimonials are both
 * unnecessary and risky. These four numbers are counted straight out of the
 * database on request, so every one of them is true and checkable — nothing is
 * rounded up, projected, or padded with a baseline.
 *
 * That also means they start small. The component simply doesn't render a
 * figure that isn't there yet rather than dressing one up, and hides itself
 * entirely if nothing has any substance — an empty stat bar is better than a
 * dishonest one.
 */
const STAT_LABELS = [
  { key: 'itineraries_generated', label: 'itineraries planned' },
  { key: 'destinations_covered', label: 'destinations covered' },
  { key: 'articles_published', label: 'travel guides published' },
  { key: 'places_catalogued', label: 'places catalogued' },
];

const SiteStats = ({ className = '' }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getSiteStats()
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch(() => {
        // Social proof is a nice-to-have; never let it break the landing page.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!stats) return null;

  const shown = STAT_LABELS.filter(({ key }) => (stats[key] || 0) > 0);
  if (shown.length === 0) return null;

  return (
    <section className={`py-10 ${className}`} aria-label="ItinerarySeek by the numbers">
      <div className="mx-auto max-w-5xl px-4">
        <div className="grid grid-cols-2 gap-6 sm:gap-8 md:grid-cols-4">
          {shown.map(({ key, label }) => (
            <div key={key} className="text-center">
              <div className="font-serif text-3xl font-bold text-neutral-900 sm:text-4xl">
                {(stats[key] || 0).toLocaleString()}
              </div>
              <div className="mt-1 text-sm leading-snug text-neutral-500">{label}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-neutral-400">
          Counted live from our database — no rounding, no estimates.
        </p>
      </div>
    </section>
  );
};

export default SiteStats;
